import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Users } from "lucide-react";

export default function UpcomingEvents({ events, onReserve }) {
  return (
    <section
      id="events"
      className="py-24 sm:py-32"
      data-testid="upcoming-events-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-mono text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(var(--accent))] mb-4">
            What's next
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-medium">
            Upcoming events
          </h2>
        </motion.div>

        {events.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="events-empty">
            <p className="text-lg">Events loading...</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 stagger-children">
            {events.map((event, i) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`event-card-${i}`}
              >
                <div className="bg-card border border-border/40 rounded-sm p-6 sm:p-8 h-full flex flex-col shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]">
                  <div className="flex items-start justify-between mb-4">
                    <Badge
                      variant="secondary"
                      className="rounded-full px-3 py-1 text-xs uppercase tracking-wider"
                    >
                      {event.theme}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-medium">
                      {event.spotsLeft ?? 0} Men &amp; {event.spotsLeftFemale ?? 0} Women spots left
                    </span>
                  </div>

                  <h3 className="font-heading text-xl tracking-tight font-medium mb-4">
                    {event.name}
                  </h3>

                  {event.description && (
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      {event.description}
                    </p>
                  )}

                  <div className="space-y-2 mb-5 flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      {event.area}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      {event.date} / {event.time}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      Age {event.age_band}
                    </div>
                  </div>

                  {event.tags && event.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-5">
                      {event.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-[11px] font-mono tracking-wider uppercase text-muted-foreground bg-muted px-2 py-0.5 rounded-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <Button
                    onClick={() => onReserve(event)}
                    className="w-full rounded-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
                    data-testid={`event-reserve-btn-${i}`}
                  >
                    Reserve
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
